import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mic, MicOff } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { JOURNAL } from "@/constants/testIds/app";

const PROMPTS = [
  "What has been sitting with you today?",
  "Where did your mind wander this week?",
  "What are you carrying that you'd like to set down?",
  "Which small thing brought you comfort recently?",
];

export default function Journal() {
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState("");
  const [voiceSupported, setVoiceSupported] = useState(false);
  const recognitionRef = useRef(null);
  const baseTextRef = useRef("");
  const prompt = PROMPTS[new Date().getDay() % PROMPTS.length];

  useEffect(() => {
    const SR = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
    if (!SR) return;
    setVoiceSupported(true);
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = navigator.language || "en-US";

    rec.onresult = (event) => {
      let finalChunk = "";
      let interimChunk = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const r = event.results[i];
        if (r.isFinal) finalChunk += r[0].transcript;
        else interimChunk += r[0].transcript;
      }
      if (finalChunk) {
        const joined = (baseTextRef.current + (baseTextRef.current && !baseTextRef.current.endsWith(" ") ? " " : "") + finalChunk).trimStart();
        baseTextRef.current = joined;
        setText(joined);
        setInterim("");
      } else {
        setInterim(interimChunk);
      }
    };

    rec.onerror = (e) => {
      setListening(false);
      setInterim("");
      const kind = e?.error;
      if (kind === "not-allowed" || kind === "service-not-allowed") {
        toast.error("Microphone permission was declined");
      } else if (kind === "no-speech") {
        toast.message("No speech detected", { description: "Try again when you're ready." });
      } else if (kind !== "aborted") {
        toast.error("Voice mode stopped");
      }
    };

    rec.onend = () => {
      setListening(false);
      setInterim("");
    };

    recognitionRef.current = rec;
    return () => {
      try { rec.abort(); } catch {}
    };
  }, []);

  const toggleListening = () => {
    const rec = recognitionRef.current;
    if (!rec) return;
    if (listening) {
      try { rec.stop(); } catch {}
      setListening(false);
      return;
    }
    baseTextRef.current = text;
    setInterim("");
    try {
      rec.start();
      setListening(true);
    } catch (err) {
      toast.error("Could not start voice mode");
    }
  };

  const onTextChange = (e) => {
    const v = e.target.value;
    setText(v);
    baseTextRef.current = v;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const trimmed = (text || "").trim();
    if (trimmed.length < 4) {
      toast.error("Give it a few words to work with");
      return;
    }
    if (listening) {
      try { recognitionRef.current?.stop(); } catch {}
    }
    setBusy(true);
    try {
      const { data: entry } = await api.post("/journal", { text: trimmed });
      toast.message("Reading your reflection…", { description: "Themes are being drawn out." });
      const { data: rec } = await api.post("/recommend", { source_type: "journal", source_id: entry.id });
      navigate(`/recommendations/${rec.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const displayText = text + (interim ? (text && !text.endsWith(" ") ? " " : "") + interim : "");

  return (
    <div data-testid={JOURNAL.container} className="max-w-3xl">
      <div className="label-caps mb-4">Journal entry</div>
      <h1 className="font-serif-display text-4xl sm:text-5xl text-[#2C2520] tracking-tight">
        Write, or speak.
      </h1>
      <p className="mt-5 text-[#2C2520]/70 leading-relaxed italic font-serif-display text-lg">
        {prompt}
      </p>
      {voiceSupported && (
        <p className="mt-3 text-sm text-[#2C2520]/55">
          When writing feels too heavy, tap the mic and speak. Your words will appear as you go.
        </p>
      )}

      <form onSubmit={onSubmit} className="mt-8 space-y-6">
        <div className="relative">
          <textarea
            data-testid={JOURNAL.text}
            value={displayText}
            onChange={onTextChange}
            rows={14}
            placeholder="Let the sentences meander. Nothing here is graded."
            className={
              "w-full p-6 pr-16 rounded-[12px] bg-[#F5F2EA] border text-[#2C2520] focus:outline-none focus:bg-[#FDFBF7] transition-colors font-serif-display text-lg leading-relaxed " +
              (listening ? "border-[#4A5D4E] ring-2 ring-[#4A5D4E]/25" : "border-[#E8E3D5] focus:border-[#4A5D4E]")
            }
          />
          {voiceSupported && (
            <button
              type="button"
              data-testid="journal-voice-toggle"
              onClick={toggleListening}
              aria-label={listening ? "Stop voice mode" : "Start voice mode"}
              className={
                "absolute top-4 right-4 w-11 h-11 rounded-full inline-flex items-center justify-center transition-all " +
                (listening
                  ? "bg-[#4A5D4E] text-white shadow-[0_2px_12px_rgba(74,93,78,0.4)]"
                  : "bg-[#FDFBF7] border border-[#E8E3D5] text-[#2C2520] hover:bg-[#EDE8DB]")
              }
            >
              {listening ? <MicOff className="w-5 h-5" strokeWidth={1.5} /> : <Mic className="w-5 h-5" strokeWidth={1.5} />}
              {listening && (
                <span className="absolute inset-0 rounded-full ring-2 ring-[#4A5D4E]/30 animate-ping" />
              )}
            </button>
          )}
        </div>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 text-sm text-[#2C2520]/60">
            <span data-testid="journal-char-count">{text.length} characters</span>
            {listening && (
              <span data-testid="journal-voice-status" className="inline-flex items-center gap-1.5 text-[#4A5D4E]">
                <span className="w-2 h-2 rounded-full bg-[#4A5D4E] animate-pulse" />
                Listening
              </span>
            )}
          </div>
          <button
            data-testid={JOURNAL.submit}
            type="submit"
            disabled={busy}
            className="px-7 py-3.5 rounded-[12px] bg-[#4A5D4E] text-white hover:bg-[#3D4D40] transition-transform active:scale-95 disabled:opacity-60"
          >
            {busy ? "Reading gently…" : "Reflect & receive picks"}
          </button>
        </div>
        {!voiceSupported && (
          <p className="text-xs text-[#2C2520]/50">
            Voice mode is available in Chrome, Safari, and Edge.
          </p>
        )}
      </form>
    </div>
  );
}
